import React, { useMemo, useState } from "react";
import { Alert, Col, Row, Spinner } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useQuery } from "react-query";
import CinemaFilter from "../../components/Common/CinemaFilter";
import DateRangeFilter from "../../components/Common/DateRangeFilter";
import { adminService } from "../../services/adminService";
import type {
  DashboardAnalytics,
  DashboardCinema,
  DashboardPeriodLabel,
  DashboardRecentBooking,
  DashboardStats,
} from "../../types/dashboard";
import {
  DashboardFilterProvider,
  useDashboardFilter,
} from "./DashboardFilterContext";
import DashboardBookingPreviewModal from "./DashboardBookingPreviewModal";
import { getAnalyticsPeriod, getCardTitles, getTopCinemaSubtext } from "./dashboardCopy";
import QuickStatsPanel from "./QuickStatsPanel";
import RecentBookings from "./RecentBookings";
import RevenueChart from "./RevenueChart";
import StatCard from "./StatCard";
import StatsCards from "./StatsCards";
import TopMoviesList from "./TopMoviesList";
import { formatCompactCount, formatCompactCurrency } from "../../utils/format";

const DashboardInner: React.FC = () => {
  const { period, setPeriod, cinemaId, setCinemaId, dateRange } =
    useDashboardFilter();
  const [previewBooking, setPreviewBooking] =
    useState<DashboardRecentBooking | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const analyticsPeriodKey = getAnalyticsPeriod(period.period as DashboardPeriodLabel);

  const {
    data: statsResponse,
    isLoading: statsLoading,
    isError: statsError,
    error: statsErrorObj,
  } = useQuery(
    ["dashboardStats", period, cinemaId],
    () =>
      adminService.getDashboardStats(
        period.period,
        dateRange,
        cinemaId,
      ),
    {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  const {
    data: analyticsResponse,
    isLoading: analyticsLoading,
    isError: analyticsError,
  } = useQuery(
    ["analytics", analyticsPeriodKey, cinemaId, dateRange, period.period],
    () =>
      adminService.getAnalytics(
        analyticsPeriodKey,
        "all",
        dateRange,
        cinemaId,
        period.period,
      ),
    {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  const {
    data: cinemasResponse,
  } = useQuery(
    ["cinemas-for-label", { limit: 1000 }],
    () => adminService.getCinemas({ limit: 1000 }),
    {
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
    },
  );

  const stats: DashboardStats | null = useMemo(() => {
    if (!statsResponse?.success) return null;
    return statsResponse.data as DashboardStats;
  }, [statsResponse]);

  const analytics: DashboardAnalytics | null = useMemo(() => {
    if (!analyticsResponse?.success) return null;
    return analyticsResponse.data as DashboardAnalytics;
  }, [analyticsResponse]);

  const cinemas: DashboardCinema[] = useMemo(() => {
    if (!cinemasResponse?.success || !cinemasResponse.data) return [];
    const raw = Array.isArray(cinemasResponse.data)
      ? cinemasResponse.data
      : ((cinemasResponse.data as any).cinemas ??
        (cinemasResponse.data as any).data ??
        []);
    return (raw as any[])
      .filter((c: any) => c && c.id)
      .map((c: any) => ({
        id: String(c.id),
        name: String(c.name ?? ""),
      }));
  }, [cinemasResponse]);

  const selectedCinemaName = useMemo(() => {
    if (!cinemaId) return null;
    return cinemas.find((c) => c.id === cinemaId)?.name ?? null;
  }, [cinemaId, cinemas]);

  const topCinemaSubtext = useMemo(() => {
    if (!stats) return null;
    return getTopCinemaSubtext(stats.periodLabel, selectedCinemaName);
  }, [stats, selectedCinemaName]);

  const isLoading = statsLoading || analyticsLoading;
  const hasError = statsError || analyticsError;
  const errorMessage =
    (statsErrorObj as any)?.message ??
    "Failed to load dashboard data.";

  const handleRowClick = (booking: DashboardRecentBooking) => {
    setPreviewBooking(booking);
    setShowPreviewModal(true);
  };

  return (
    <div className="fade-in">
      <Helmet>
        <title>Dashboard | MyCinemas Admin</title>
      </Helmet>

      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="mb-1">Dashboard</h2>
          <p className="text-muted mb-0">
            Welcome back! Here's what's happening with your cinema business.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          {/* <DateRangeFilter value={period} onChange={setPeriod} />
          <CinemaFilter value={cinemaId} onChange={setCinemaId} /> */}
        </div>
      </div>

      {hasError ? (
        <Alert variant="danger" className="mb-3">
          {errorMessage}
        </Alert>
      ) : null}

      {isLoading && !stats ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: 320 }}
        >
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading…</span>
          </Spinner>
        </div>
      ) : null}

      {stats ? (
        <>
          <StatsCards stats={stats} />

          {!selectedCinemaName && stats.periodTopCinema ? (
            <Row className="mb-4">
              <Col xs={12} className="mb-3">
                <StatCard
                  layout="name"
                  fullWidth
                  title={getCardTitles(stats.periodLabel).topCinema}
                  value={
                    stats.periodTopCinema.cinemaName
                      ? stats.periodTopCinema.cinemaName
                      : "—"
                  }
                  subtext={
                    topCinemaSubtext
                      ? `${formatCompactCurrency(stats.periodTopCinema.revenue)} · ${formatCompactCount(stats.periodTopCinema.bookings)} bookings · ${topCinemaSubtext}`
                      : `${formatCompactCurrency(stats.periodTopCinema.revenue)} · ${formatCompactCount(stats.periodTopCinema.bookings)} bookings`
                  }
                  icon="bi-building"
                  color="secondary"
                />
              </Col>
            </Row>
          ) : null}

          <Row className="mb-4">
            <RevenueChart
              analytics={analytics}
              period={stats.periodLabel}
              selectedCinemaName={selectedCinemaName}
            />
          </Row>

          <Row className="mb-4">
            <Col xs={12} className="mb-3">
              <TopMoviesList
                analytics={analytics}
                period={stats.periodLabel}
                cinemaName={selectedCinemaName}
                cinemaId={cinemaId}
                dateRange={dateRange}
              />
            </Col>
          </Row>

          <Row className="mb-4">
            <QuickStatsPanel
              stats={stats}
              activeMovies={analytics?.activeMovies ?? 0}
            />
          </Row>

          <Row className="mb-4">
            <Col xs={12} className="mb-3">
              <RecentBookings
                bookings={analytics?.recentBookings ?? []}
                analyticsLoading={analyticsLoading}
                onRowClick={handleRowClick}
              />
            </Col>
          </Row>
        </>
      ) : null}

      <DashboardBookingPreviewModal
        show={showPreviewModal}
        onHide={() => setShowPreviewModal(false)}
        booking={previewBooking}
      />
    </div>
  );
};

const Dashboard: React.FC = () => (
  <DashboardFilterProvider>
    <DashboardInner />
  </DashboardFilterProvider>
);

export default Dashboard;